import { useState, useEffect } from 'react';
import { Job, JobStatus } from '@recruitai/shared';
import { apiClient } from '../lib/api-client';

interface UseJobsResult {
  jobs: Job[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface ListJobsResponse {
  jobs: Job[];
  nextToken?: string;
}

export function useJobs(statusFilter?: JobStatus): UseJobsResult {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchJobs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const endpoint = statusFilter 
        ? `/jobs?status=${statusFilter}` 
        : '/jobs';
      
      const response = await apiClient.get<ListJobsResponse>(endpoint);
      setJobs(response.jobs);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch jobs'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [statusFilter]);

  return {
    jobs,
    isLoading,
    error,
    refetch: fetchJobs,
  };
}

interface UseJobResult {
  job: Job | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useJob(jobId: string): UseJobResult {
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchJob = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiClient.get<Job>(`/jobs/${jobId}`);
      setJob(response);
    } catch (err) {
      console.error('Failed to fetch job:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch job'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  return {
    job,
    isLoading,
    error,
    refetch: fetchJob,
  };
}
